import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const next = params.get("next");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      if (next) nav(next);
      else nav(u.role === "admin" || u.role === "staff" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid place-items-center px-6 py-16">
      <div className="w-full max-w-md eyw-card p-10">
        <img src="https://customer-assets-wrfwihn1.emergentagent.net/job_eyw-admin-suite/artifacts/ly5amm9a_EnhancingYourWorld2-02_compass57x57.png" alt="Enhancing Your World" className="h-12 w-12 rounded-full mb-6 object-contain" />
        <p className="eyw-label mb-1">Member Login</p>
        <h1 className="font-serif text-3xl text-[#1A242B] mb-6">Welcome back.</h1>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-sm text-[#4A5D6B] mb-1 block">Email</label>
            <input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-[#E5E0D8] px-4 py-3 outline-none focus:border-[#144F75]" />
          </div>
          <div>
            <label className="text-sm text-[#4A5D6B] mb-1 block">Password</label>
            <div className="relative">
              <input data-testid="login-password-input" type={showPassword ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-[#E5E0D8] px-4 py-3 pr-11 outline-none focus:border-[#144F75]" />
              <button type="button" data-testid="login-password-toggle" onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-[#8FA89B] hover:text-[#144F75]">
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          {error && <p data-testid="login-error" className="text-sm text-red-600">{error}</p>}
          <button data-testid="login-submit-btn" type="submit" disabled={loading} className="eyw-btn w-full disabled:opacity-60">
            {loading && <Loader2 className="animate-spin" size={18} />} Sign In
          </button>
          <Link to="/forgot-password" data-testid="login-forgot-link" className="block text-center text-sm text-[#144F75] mt-2">Forgot your password?</Link>
        </form>
        <p className="text-sm text-[#4A5D6B] mt-6 text-center">
          New here? <Link to={`/join${email ? `?email=${encodeURIComponent(email)}` : ""}`} className="text-[#144F75] font-medium">Join as a Discovery Member</Link>
        </p>
      </div>
    </div>
  );
}
